import {
  JsonView,
  allExpanded,
  type defaultStyles,
} from "react-json-view-lite";

type JsonViewStyleProps = typeof defaultStyles;

const baseStyles: JsonViewStyleProps = {
  container: "font-mono text-[12px] leading-relaxed",
  basicChildStyle: "pl-4 border-l border-border/30 ml-1",
  label: "text-muted-foreground font-medium mr-1",
  clickableLabel:
    "text-muted-foreground font-medium mr-1 cursor-pointer hover:text-foreground",
  nullValue: "text-muted-foreground/50 italic",
  undefinedValue: "text-muted-foreground/50 italic",
  numberValue: "text-blue-600 dark:text-blue-400",
  stringValue: "text-emerald-600 dark:text-emerald-400",
  booleanValue: "text-violet-600 dark:text-violet-400",
  otherValue: "text-foreground",
  punctuation: "text-muted-foreground/60",
  expandIcon: "hidden",
  collapseIcon: "hidden",
  collapsedContent:
    "text-muted-foreground/40 cursor-pointer hover:text-muted-foreground transition-colors",
  childFieldsContainer: "",
  noQuotesForStringValues: false,
  quotesForFieldNames: false,
  stringifyStringValues: false,
  ariaLables: { collapseJson: "Collapse", expandJson: "Expand" },
};

// The lib renders empty spans for the icons and the collapsed node, drawing the
// glyphs via its own CSS ::after. Since we replace the classes we must redraw
// them: triangles for expand/collapse and "…" for collapsed content.
const navigableStyles: JsonViewStyleProps = {
  ...baseStyles,
  expandIcon:
    "inline-block w-4 cursor-pointer select-none text-muted-foreground after:content-['▸']",
  collapseIcon:
    "inline-block w-4 cursor-pointer select-none text-muted-foreground after:content-['▾']",
  collapsedContent:
    "mx-1 cursor-pointer select-none text-muted-foreground/60 hover:text-muted-foreground transition-colors after:content-['…']",
};

export interface NoxusJsonViewProps {
  data: object | unknown[];
  // "all" fully expands; a number expands nodes up to that depth. Default "all".
  expand?: "all" | number;
  // Show expand/collapse triangles so the tree can be navigated. Set false for
  // a static, fully-expanded read-only display. Default true.
  navigable?: boolean;
}

export function tryParseJsonObject(value: unknown): object | unknown[] | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return parsed !== null && typeof parsed === "object"
      ? (parsed as object | unknown[])
      : null;
  } catch {
    return null;
  }
}

const shouldExpand = (expand: "all" | number) =>
  expand === "all" ? allExpanded : (level: number) => level < expand;

export function NoxusJsonView({
  data,
  expand = "all",
  navigable = true,
}: NoxusJsonViewProps) {
  return (
    <JsonView
      // shouldExpandNode is only read on mount; remount when the expand target
      // changes so expand-all / collapse-all toggles actually re-apply.
      key={String(expand)}
      data={data}
      shouldExpandNode={shouldExpand(expand)}
      clickToExpandNode={navigable}
      style={navigable ? navigableStyles : baseStyles}
    />
  );
}
